/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import { NextComponentType, NextPageContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { metaTags, linkTags, getPaginationLinks, removeMetaTags } from 'utils/seoHead';

import ThemeProvider, { ThemeEnum } from 'ui/src/components/Theme';

import BairesDevHeader from 'layout/src/components/header';
import DeprecatedBairesDevHeader from 'layout/src/components/header_deprecated';
import BairesDevFooter from 'layout/src/components/footer';
import ButtonArrowIcon from 'ui/src/icons/ButtonArrowIcon';
import EmailIcon from 'ui/src/icons/EmailIcon';
import type { FooterMenu } from 'layout/src/data/FooterMenuType';

import { getMenuTree } from 'utils/shared';
import seoScriptProps from 'utilities/src/seoScriptProps';

import mutiny from 'tracking/src/mutiny';

import { MenuLocationEnum } from 'gql/graphql-operations';
import ButtonLink from 'ui/src/components/Button/ButtonLink';

import { parseImageUrl } from 'utils/imageUtils';
import CookieBar from 'layout/src/components/cookieBar';
import addUrlHostName from 'ui/src/utils/addUrlHostname';
import extractText from 'utilities/src/extractText';

import Navigation from 'components/Navigation';

import { newHeaderData, deprecatedMenuData } from 'data/mocks';

export default function withLayoutHOC(Component: NextComponentType<NextPageContext, any, {}>) {
  const shouldUseNewHeader = process.env.NEXT_PUBLIC_SHOULD_USE_NEW_HEADER === 'true';

  return function ComponentWithFooter(props: any) {
    const {
      menuItems,
      seo,
      year,
      searchEngineDiscovery,
      isPaginated,
      removeBlogUrl,
      bgFooter = 'Light',
      ...componentProps
    } = props;

    // @TODO get menu data from wp
    // const mainMenu = getMenuTree(menuItems, MenuLocationEnum.Main);
    const blogMenu = getMenuTree(menuItems, MenuLocationEnum.Blog);

    const footerMenus: FooterMenu[] = [
      {
        title: menuItems[MenuLocationEnum.Footer_1].name,
        menuItems: getMenuTree(menuItems, MenuLocationEnum.Footer_1),
      },
      {
        title: menuItems[MenuLocationEnum.Footer_2].name,
        menuItems: getMenuTree(menuItems, MenuLocationEnum.Footer_2),
      },
      {
        title: menuItems[MenuLocationEnum.Footer_3].name,
        menuItems: getMenuTree(menuItems, MenuLocationEnum.Footer_3),
      },
    ];

    const pageTitle = extractText(seo?.openGraph?.title ?? componentProps?.pageName);

    useEffect(() => {
      mutiny.sendMutinyExperienceToSegment(process.env.NEXT_PUBLIC_GA4);
    }, [pageTitle]);

    const scriptProps = seoScriptProps(seo?.jsonLd, parseImageUrl);
    const { asPath } = useRouter();
    const path = asPath.split('#')[0].split('?')[0];
    const host = `${process.env.NEXT_PUBLIC_HOSTNAME}${removeBlogUrl ? '' : process.env.NEXT_PUBLIC_BASE_PATH}`;
    const canonicalLink = { href: `${host}${path}`, rel: 'canonical' };
    const paginationLinks = isPaginated
      ? getPaginationLinks(path, componentProps?.page ?? 1, componentProps?.total ?? 0, host)
      : [];

    useEffect(
      () => () => {
        const seoElements = seo ? metaTags(seo) : [];
        removeMetaTags(seoElements);
      },
      [seo],
    );

    return (
      <>
        <Head>
          {seo && metaTags(seo)}
          {scriptProps && <script {...scriptProps} />}
          {linkTags([canonicalLink, ...paginationLinks])}
          {(!searchEngineDiscovery || !seo || process.env.NEXT_PUBLIC_ENABLE_ROBOTS === 'false') && (
            <meta name="robots" key="robots" content="nofollow, noindex, noarchive, noimageindex, nosnippet" />
          )}
        </Head>
        <ThemeProvider theme={ThemeEnum.Site}>
          {shouldUseNewHeader ? (
            <BairesDevHeader
              animatedHeader={false}
              enableHeaderBehavior
              menuItems={newHeaderData}
              logoPath={`${process.env.NEXT_PUBLIC_BASE_PATH}/bairesdev-logo-light.svg`}
              headerLinkChildren={
                <>
                  <div className="bg-site-neutrals-50 mx-5 w-0.5 self-stretch rounded" />
                  <a
                    href={`${process.env.NEXT_PUBLIC_HOSTNAME}${process.env.NEXT_PUBLIC_BASE_PATH}`}
                    className="site-display-xs text-site-neutrals-800 font-light"
                  >
                    Blog
                  </a>
                </>
              }
            />
          ) : (
            <DeprecatedBairesDevHeader
              animatedHeader={false}
              enableHeaderBehavior
              menuItems={deprecatedMenuData}
              logoPath={`${process.env.NEXT_PUBLIC_BASE_PATH}/bairesdev-logo-light.svg`}
              logoSize={{ width: 142, height: 21 }}
              superMenu
              headerLinkClasses="flex items-center gap-8 h-11"
              headerLinkChildren={
                <>
                  <div className="bg-site-neutrals-50 w-0.5 self-stretch rounded" />
                  <a
                    href={`${process.env.NEXT_PUBLIC_HOSTNAME}${process.env.NEXT_PUBLIC_BASE_PATH}`}
                    className="site-display-xs text-site-neutrals-800 font-light"
                  >
                    Blog
                  </a>
                </>
              }
              CtaButton={
                <ButtonLink<ThemeEnum.Site>
                  variant="Primary"
                  variantColor="Black"
                  size="Medium"
                  classes="ml-8 mdL:ml-2"
                  href="https://www.bairesdev.com/start/basic-details/"
                >
                  Schedule a Call
                </ButtonLink>
              }
            />
          )}

          <Navigation
            list={{
              title: 'Category',
              links: blogMenu.map((item) => ({ label: item.label, href: item.path })),
            }}
            visibleInPaths={[
              '/',
              '/author/[slug]/[[...page]]',
              '/category/[slug]/[[...page]]',
              '/page/[page]',
              '/search',
              '/search/not-found/page/[page]',
              '/tag/[slug]/[[...page]]',
              '/authors/[[...page]]',
            ]}
          />
          <Component {...componentProps} />
          <BairesDevFooter<ThemeEnum.Site>
            year={year}
            contactusUrl={addUrlHostName('/contact-us/')}
            placeholder="name@email.com"
            label="Your e-mail address"
            hasSocialTitle
            bgFooter={bgFooter}
            logoPath={`${process.env.NEXT_PUBLIC_BASE_PATH ?? ''}/bairesdev-logo-footer.svg`}
            logoSize={[60, 40]}
            menus={footerMenus}
            subscriptionApi={`${process.env.NEXT_PUBLIC_BASE_PATH}/api/subscribe/`}
            buttonsProps={{
              scheduleCallBtn: {
                variant: 'Primary',
                icon: ButtonArrowIcon,
              },
              contactUsBtn: {
                variant: 'Secondary',
                variantColor: 'Black',
              },
              subscriptionBtn: {
                variant: 'Secondary',
                variantColor: 'Black',
                icon: EmailIcon,
                iconPosition: 'right',
              },
            }}
            withFullStops
          />
          <CookieBar />
        </ThemeProvider>
      </>
    );
  };
}
